import React, { useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AppRoutes, { prefixUrl, SECURITY_PATH } from "routing/Routes";
import SecurityRoutes, { SECURITY_VIEW_ID_PREFIX } from "security-app/routing/Routes";
import {
  Overview,
  UserActivity,
  HostActivity,
  NetworkActivity,
  ActivityPage,
  DetectorConfiguration,
  SigmaConfig,
  Investigations,
  InvestigationDetails,
  InvestigationsConfig,
  AssetsList,
  AssetsConfig,
  AssetSources,
  AssetSourcesForms,
  SecurityOverviewPage,
  VulnerabilityScanners
} from "security-app/pages";
import AppConfig from "util/AppConfig";
import usePermissions from "hooks/usePermissions";
import LegacySecurityEventsRedirect from "security-app/pages/security-events/LegacySecurityEventsRedirect";
export const RouterRoutes = [
  { path: SecurityRoutes.THREAT_COVERAGE, component: /* @__PURE__ */ React.createElement(SecurityOverviewPage, null) },
  { path: SecurityRoutes.ACTIVITY.OVERVIEW, component: /* @__PURE__ */ React.createElement(ActivityPage, null) },
  { path: SecurityRoutes.ACTIVITY.USER_ACTIVITY, component: /* @__PURE__ */ React.createElement(ActivityPage, null) },
  {
    path: SecurityRoutes.ACTIVITY.ASSET_DRILLDOWN,
    component: /* @__PURE__ */ React.createElement(ActivityPage, null)
  },
  { path: SecurityRoutes.ACTIVITY.HOST_ACTIVITY, component: /* @__PURE__ */ React.createElement(ActivityPage, null) },
  {
    path: SecurityRoutes.ACTIVITY.NETWORK_ACTIVITY,
    component: /* @__PURE__ */ React.createElement(ActivityPage, null)
  },
  {
    path: SecurityRoutes.ACTIVITY.OVERVIEW,
    entityId: `${SECURITY_VIEW_ID_PREFIX}overview`,
    component: /* @__PURE__ */ React.createElement(Overview, null)
  },
  {
    path: SecurityRoutes.ACTIVITY.USER_ACTIVITY,
    entityId: `${SECURITY_VIEW_ID_PREFIX}user-activity`,
    component: /* @__PURE__ */ React.createElement(UserActivity, null)
  },
  {
    path: SecurityRoutes.ACTIVITY.HOST_ACTIVITY,
    entityId: `${SECURITY_VIEW_ID_PREFIX}host-activity`,
    component: /* @__PURE__ */ React.createElement(HostActivity, null)
  },
  {
    path: SecurityRoutes.ACTIVITY.NETWORK_ACTIVITY,
    entityId: `${SECURITY_VIEW_ID_PREFIX}network-activity`,
    component: /* @__PURE__ */ React.createElement(NetworkActivity, null)
  },
  { path: SecurityRoutes.ANOMALIES.LIST, component: /* @__PURE__ */ React.createElement(DetectorConfiguration, null) },
  {
    path: SecurityRoutes.ANOMALIES.DETECTORS.LIST,
    component: /* @__PURE__ */ React.createElement(DetectorConfiguration, null)
  },
  { path: SecurityRoutes.SIGMA.CONFIGURATION, component: /* @__PURE__ */ React.createElement(SigmaConfig, null) },
  // Sigma rules are now managed through Event Definitions, redirect the removed legacy page.
  {
    path: SecurityRoutes.SIGMA.RULES,
    component: /* @__PURE__ */ React.createElement(Navigate, { replace: true, to: `${AppRoutes.ALERTS.DEFINITIONS.LIST}?page=1&filters=type%3Dsigma-v1` })
  },
  { path: SecurityRoutes.INVESTIGATIONS.LIST, component: /* @__PURE__ */ React.createElement(Investigations, null) },
  {
    path: SecurityRoutes.INVESTIGATIONS.details(":id"),
    component: /* @__PURE__ */ React.createElement(InvestigationDetails, null)
  },
  {
    path: SecurityRoutes.INVESTIGATIONS.show(":id"),
    component: /* @__PURE__ */ React.createElement(Investigations, null)
  },
  // The investigations list selects the archived investigations through its layout variant.
  {
    path: SecurityRoutes.INVESTIGATIONS.ARCHIVED,
    component: /* @__PURE__ */ React.createElement(Navigate, { replace: true, to: `${SecurityRoutes.INVESTIGATIONS.LIST}?layout_variant=archived` })
  },
  {
    path: SecurityRoutes.INVESTIGATIONS.CONFIGURATION,
    component: /* @__PURE__ */ React.createElement(InvestigationsConfig, null)
  },
  { path: SecurityRoutes.ASSETS.list(":assetType"), component: /* @__PURE__ */ React.createElement(AssetsList, null) },
  {
    path: SecurityRoutes.ASSETS.details(":assetType", ":id"),
    component: /* @__PURE__ */ React.createElement(AssetsList, null)
  },
  { path: SecurityRoutes.ASSETS.CONFIGURATION, component: /* @__PURE__ */ React.createElement(AssetsConfig, null) },
  { path: SecurityRoutes.ASSETS.SOURCES.LIST, component: /* @__PURE__ */ React.createElement(AssetSources, null) },
  {
    path: SecurityRoutes.ASSETS.SOURCES.forms(":sourceType"),
    component: /* @__PURE__ */ React.createElement(AssetSourcesForms, null),
    permissions: "asset:edit"
  },
  {
    path: SecurityRoutes.ASSETS.SOURCES.form(":sourceType", ":sourceId"),
    component: /* @__PURE__ */ React.createElement(AssetSourcesForms, null),
    permissions: "asset:edit"
  },
  {
    path: SecurityRoutes.ASSETS.SOURCES.formMappings(":sourceType", ":sourceId"),
    component: /* @__PURE__ */ React.createElement(AssetSourcesForms, null),
    permissions: "asset:edit"
  },
  {
    path: SecurityRoutes.ASSETS.SOURCES.formMapping(":sourceType", ":sourceId", ":mapId"),
    component: /* @__PURE__ */ React.createElement(AssetSourcesForms, null),
    permissions: "asset:edit"
  },
  {
    path: SecurityRoutes.ASSETS.SCANNERS.LIST,
    component: /* @__PURE__ */ React.createElement(VulnerabilityScanners, null)
  },
  // We removed the security event pages in favor of the default alert pages.
  // In the following cases we redirect the old security event routes to the default alert routes.
  // These redirects can be removed at some point.
  {
    path: SecurityRoutes.SECURITY_EVENTS.EVENT_PROCEDURES.list(":viewType"),
    component: /* @__PURE__ */ React.createElement(
      LegacySecurityEventsRedirect,
      {
        from: SecurityRoutes.SECURITY_EVENTS.EVENT_PROCEDURES.list(""),
        to: SecurityRoutes.CORE_EVENTS.EVENT_PROCEDURES.LIST("")
      }
    )
  },
  {
    path: SecurityRoutes.SECURITY_EVENTS.EVENT_PROCEDURES.executeAction(),
    component: /* @__PURE__ */ React.createElement(
      LegacySecurityEventsRedirect,
      {
        from: SecurityRoutes.SECURITY_EVENTS.EVENT_PROCEDURES.executeAction(),
        to: SecurityRoutes.CORE_EVENTS.EVENT_PROCEDURES.executeAction()
      }
    )
  },
  {
    path: SecurityRoutes.SECURITY_EVENTS.ALERTS.LIST,
    component: /* @__PURE__ */ React.createElement(LegacySecurityEventsRedirect, { from: SecurityRoutes.SECURITY_EVENTS.ALERTS.LIST, to: AppRoutes.ALERTS.LIST })
  },
  {
    path: SecurityRoutes.SECURITY_EVENTS.DEFINITIONS,
    component: /* @__PURE__ */ React.createElement(
      LegacySecurityEventsRedirect,
      {
        from: SecurityRoutes.SECURITY_EVENTS.DEFINITIONS,
        to: AppRoutes.ALERTS.DEFINITIONS.LIST
      }
    )
  },
  {
    path: SecurityRoutes.SECURITY_EVENTS.NOTIFICATIONS,
    component: /* @__PURE__ */ React.createElement(
      LegacySecurityEventsRedirect,
      {
        from: SecurityRoutes.SECURITY_EVENTS.NOTIFICATIONS,
        to: AppRoutes.ALERTS.NOTIFICATIONS.LIST
      }
    )
  }
];
const QUALIFIED_SECURITY_PATH = prefixUrl(SECURITY_PATH);
const Router = () => {
  const { isPermitted } = usePermissions();
  const availableRoutes = useMemo(
    () => RouterRoutes.filter(
      ({ permissions, featureFlag }) => isPermitted(permissions) && (!featureFlag || AppConfig.isFeatureEnabled(featureFlag))
    ),
    [isPermitted]
  );
  return /* @__PURE__ */ React.createElement(Routes, null, availableRoutes.map(({ path, component }) => /* @__PURE__ */ React.createElement(Route, { key: path, path: path.slice(QUALIFIED_SECURITY_PATH.length), element: component })));
};
export default Router;
